import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  Plus,
  Building2,
  Archive,
  ArchiveRestore,
  Trash2,
  MoreVertical,
  History,
  X,
  ShieldAlert,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

export default function OrganizationsPage() {
  const [orgs, setOrgs] = useState([]);
  const [tombstones, setTombstones] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showTombstones, setShowTombstones] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    contact_email: "",
    tagline: "",
    plan: "Growth",
    primary_color: "#0F4C81",
    secondary_color: "#D4AF37",
  });

  const [confirmDelete, setConfirmDelete] = useState(null); // { org, forcing }
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(null); // { kind: 'archive' | 'delete', forcing }

  const load = async () => {
    const { data } = await api.get(`/orgs?include_archived=${includeArchived}`);
    setOrgs(data || []);
    // Tombstones (always fetch — small list, used for badge count)
    api.get("/orgs/tombstones").then((r) => setTombstones(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  const archivedCount = orgs.filter((o) => o.archived).length;
  const activeCount = orgs.filter((o) => !o.archived).length;

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const clearSelected = () => setSelected(new Set());
  const selectAllVisible = () => setSelected(new Set(orgs.map((o) => o.id)));

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/orgs", form);
      setOpen(false);
      setForm({ name: "", slug: "", contact_email: "", tagline: "", plan: "Growth", primary_color: "#0F4C81", secondary_color: "#D4AF37" });
      await load();
      toast.success("Organisation created");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const archive = async (org) => {
    try {
      await api.post(`/orgs/${org.id}/archive`);
      toast.success(`Archived ${org.name}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setConfirmArchive(null);
    }
  };

  const unarchive = async (org) => {
    try {
      await api.post(`/orgs/${org.id}/unarchive`);
      toast.success(`Restored ${org.name}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const hardDelete = async (org, force = false) => {
    try {
      await api.delete(`/orgs/${org.id}${force ? "?force=true" : ""}`);
      toast.success(`Deleted ${org.name}`);
      await load();
      setConfirmDelete(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && !force) {
        setConfirmDelete({ org, forcing: true, message: detail });
        return;
      }
      toast.error(formatApiError(detail) || err.message);
    }
  };

  const runBulk = async (force = false) => {
    if (!confirmBulk || selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      if (confirmBulk.kind === "archive") {
        const { data } = await api.post("/orgs/bulk-archive", { ids });
        toast.success(`Archived ${data.archived.length} · skipped ${data.skipped.length}`);
      } else {
        const { data } = await api.post(`/orgs/bulk-delete${force ? "?force=true" : ""}`, { ids });
        if (data.skipped.length > 0 && !force && data.skipped.some((s) => s.reason?.startsWith("has_"))) {
          // some had events — escalate
          setConfirmBulk({ kind: "delete", forcing: true, skipped: data.skipped, deleted: data.deleted });
          return;
        }
        toast.success(`Deleted ${data.deleted.length} · cascaded ${data.cascaded_events} events · skipped ${data.skipped.length}`);
      }
      clearSelected();
      setConfirmBulk(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-24">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              White-label clients
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Organisations
            </h1>
            <p className="mt-2 text-slate-500 text-sm">
              <span data-testid="org-count-active">{activeCount} active</span>
              <span className="mx-2 text-slate-300">·</span>
              <span data-testid="org-count-archived">{archivedCount} archived</span>
              <span className="mx-2 text-slate-300">·</span>
              <span data-testid="org-count-deleted">{tombstones.length} deleted</span>
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setShowTombstones(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-slate-200 text-sm text-slate-700 hover:border-[#0F4C81] transition-colors"
              data-testid="show-tombstones-button"
            >
              <History className="h-4 w-4 text-[#D4AF37]" /> Deleted ({tombstones.length})
            </button>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch
                checked={includeArchived}
                onCheckedChange={(v) => { setIncludeArchived(v); clearSelected(); }}
                data-testid="toggle-include-archived"
              />
              Show archived
            </label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="create-org-button">
                  <Plus className="h-4 w-4 mr-1" /> New organisation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Onboard a white-label client</DialogTitle>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4" data-testid="org-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Name</Label>
                      <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-name-input" />
                    </div>
                    <div>
                      <Label>Slug</Label>
                      <Input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="org-slug-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Contact email</Label>
                      <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                    </div>
                    <div>
                      <Label>Plan</Label>
                      <Input value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="org-submit-button">
                      Create
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Bulk action toolbar */}
        {selected.size > 0 && (
          <div
            className="mt-6 sticky top-2 z-20 flex items-center justify-between gap-4 rounded-xl bg-[#0A192F] text-white px-5 py-3 shadow-2xl border border-[#D4AF37]/30"
            data-testid="bulk-toolbar"
          >
            <div className="flex items-center gap-3 text-sm">
              <span className="px-2 py-1 rounded-full bg-[#D4AF37] text-[#0A192F] text-xs font-bold" data-testid="bulk-selected-count">
                {selected.size} selected
              </span>
              <button onClick={selectAllVisible} className="text-xs text-slate-300 hover:text-white" data-testid="bulk-select-all">
                Select all visible ({orgs.length})
              </button>
              <button onClick={clearSelected} className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1" data-testid="bulk-clear">
                <X className="h-3 w-3" /> Clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
                onClick={() => setConfirmBulk({ kind: "archive" })}
                data-testid="bulk-archive-button"
              >
                <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setConfirmBulk({ kind: "delete" })}
                data-testid="bulk-delete-button"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
              </Button>
            </div>
          </div>
        )}

        {/* Org grid */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {orgs.map((o) => {
            const isArchived = !!o.archived;
            const isSelected = selected.has(o.id);
            return (
              <div
                key={o.id}
                className={`relative rounded-xl bg-white border p-6 transition-all
                  ${isSelected ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/20" : isArchived ? "border-slate-200 opacity-60" : "border-slate-200 hover:-translate-y-1 hover:shadow-md"}`}
                data-testid={`org-card-${o.slug}`}
              >
                {/* Selection checkbox */}
                <div className="absolute top-3 left-3 z-10">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelect(o.id)}
                    data-testid={`org-select-${o.slug}`}
                    aria-label={`Select ${o.name}`}
                  />
                </div>

                {isArchived && (
                  <span className="absolute top-3 left-12 px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] uppercase tracking-[0.18em] font-bold" data-testid={`archived-badge-${o.slug}`}>
                    Archived
                  </span>
                )}

                <div className="flex items-start justify-between pl-7">
                  <div
                    className="h-10 w-10 rounded-md flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: o.primary_color || "#0F4C81" }}
                  >
                    {o.name[0]}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="h-8 w-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-[#0A192F] transition-colors"
                        data-testid={`org-menu-${o.slug}`}
                        aria-label={`Actions for ${o.name}`}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      {isArchived ? (
                        <DropdownMenuItem onClick={() => unarchive(o)} data-testid={`org-restore-${o.slug}`}>
                          <ArchiveRestore className="h-4 w-4 mr-2" /> Restore
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => setConfirmArchive(o)} data-testid={`org-archive-${o.slug}`}>
                          <Archive className="h-4 w-4 mr-2" /> Archive
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setConfirmDelete({ org: o, forcing: false })}
                        className="text-red-600 focus:text-red-700 focus:bg-red-50"
                        data-testid={`org-delete-${o.slug}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete permanently
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <h3 className="mt-5 text-lg font-semibold tracking-tight text-[#0A192F]">{o.name}</h3>
                <p className="mt-1 text-sm text-slate-500 font-serif italic">{o.tagline || "—"}</p>

                <div className="mt-5 flex items-center justify-between text-xs">
                  <span className="px-2 py-1 rounded-full bg-[#D4AF37]/15 text-[#0A192F] uppercase tracking-[0.18em] font-bold">
                    {o.plan || "Starter"}
                  </span>
                  <span className="text-slate-500">/{o.slug}</span>
                </div>
              </div>
            );
          })}

          {orgs.length === 0 && (
            <div className="col-span-full p-10 rounded-xl bg-white border-dashed border-2 border-slate-200 text-center">
              <Building2 className="h-6 w-6 text-slate-400 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">
                {includeArchived ? "No organisations at all." : "No active organisations."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tombstones drawer */}
      <Dialog open={showTombstones} onOpenChange={setShowTombstones}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-[#D4AF37]" /> Deleted organisations
            </DialogTitle>
          </DialogHeader>
          {tombstones.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="tombstones-empty">
              No deleted organisations yet.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto" data-testid="tombstones-list">
              <table className="w-full text-sm">
                <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 border-b">
                  <tr>
                    <th className="py-2.5 font-semibold">Organisation</th>
                    <th className="py-2.5 font-semibold">Slug</th>
                    <th className="py-2.5 font-semibold">Cascaded events</th>
                    <th className="py-2.5 font-semibold">Deleted</th>
                    <th className="py-2.5 font-semibold">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tombstones.map((t) => (
                    <tr key={t.id} data-testid={`tombstone-row-${t.organization_id}`}>
                      <td className="py-3 font-medium text-[#0A192F]">{t.name}</td>
                      <td className="py-3 text-slate-500 font-mono text-xs">/{t.slug}</td>
                      <td className="py-3 text-slate-700">{t.cascaded_event_count}</td>
                      <td className="py-3 text-slate-500 text-xs">
                        {t.deleted_at ? new Date(t.deleted_at).toLocaleString() : "—"}
                      </td>
                      <td className="py-3 text-slate-500 text-xs">{t.deleted_by_email || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <AlertDialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive organisation?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmArchive?.name}</strong> will be hidden from the active list and its events
              marked as archived. We'll send a notification to the org admin. You can restore anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archive(confirmArchive)} className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="archive-confirm">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Single delete confirmation (two-step if cascade needed) */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">
              {confirmDelete?.forcing ? "This is destructive" : "Delete permanently?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                <strong>{confirmDelete?.org?.name}</strong> will be hard-deleted and cannot be recovered.
              </span>
              {confirmDelete?.forcing && confirmDelete?.message && (
                <span className="block text-red-700 font-semibold">
                  {confirmDelete.message} This will cascade delete all events,
                  registrations, papers, certificates, and users of this organisation.
                </span>
              )}
              {!confirmDelete?.forcing && (
                <span className="block text-slate-500 text-xs">
                  If this organisation has events, you'll be asked to confirm a cascade.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => hardDelete(confirmDelete.org, confirmDelete.forcing)}
              className="bg-red-600 hover:bg-red-700"
              data-testid={confirmDelete?.forcing ? "delete-force-confirm" : "delete-confirm"}
            >
              {confirmDelete?.forcing ? "Cascade delete" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk confirmation */}
      <AlertDialog open={!!confirmBulk} onOpenChange={(o) => !o && setConfirmBulk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirmBulk?.kind === "delete" && <ShieldAlert className="h-5 w-5 text-red-600" />}
              {confirmBulk?.kind === "archive" && `Archive ${selected.size} organisation${selected.size === 1 ? "" : "s"}?`}
              {confirmBulk?.kind === "delete" && (confirmBulk?.forcing ? "Cascade delete?" : `Delete ${selected.size} organisation${selected.size === 1 ? "" : "s"}?`)}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {confirmBulk?.kind === "archive" && (
                <span>The selected organisations will be archived, their events marked archived, and their org admins notified.</span>
              )}
              {confirmBulk?.kind === "delete" && !confirmBulk?.forcing && (
                <span>Organisations <strong>without</strong> events will be deleted. Ones with events will be reported as skipped — you'll be asked to confirm a cascade for them.</span>
              )}
              {confirmBulk?.kind === "delete" && confirmBulk?.forcing && (
                <>
                  <span className="block text-red-700 font-semibold">
                    {confirmBulk.skipped?.length || 0} organisation(s) still have events. Cascade-delete will hard-delete every event, registration, paper, certificate and non-super-admin user under them.
                  </span>
                  <span className="block text-xs text-slate-500">
                    Already deleted in step 1: {confirmBulk.deleted?.length || 0}
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runBulk(confirmBulk?.forcing)}
              className={confirmBulk?.kind === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-[#0F4C81] hover:bg-[#0A3860]"}
              data-testid={confirmBulk?.forcing ? "bulk-force-confirm" : "bulk-confirm"}
            >
              {confirmBulk?.kind === "archive" ? "Archive all" : confirmBulk?.forcing ? "Cascade delete" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
